/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  VANITYNAMES.TS - Player Profile & Nickname System                        ║
 * ║  Lets players set custom names instead of 0x123...abc                     ║
 * ║  Names are a ONE-TIME purchase — persist across ALL rounds/games          ║
 * ║  Backed by Cloudflare KV for cross-device / cross-browser persistence    ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface PlayerProfile {
  address: string;
  name: string;
  avatar?: string;  // Emoji or URL
  title?: string;   // Achievement-based title
  verified?: boolean;
  paid?: boolean;    // Has paid the vanity name fee
  txHash?: string;   // Payment transaction hash
}

// Storage key for local cache
const STORAGE_KEY = 'nt3d_player_profiles';
const MY_PROFILE_KEY = 'nt3d_my_profile';

// Worker API URL for persistent name storage
const VANITY_NAMES_API = import.meta.env.VITE_VANITY_NAMES_API || 'https://nt3d-vanity-names.wurkit.workers.dev';

// Vanity name cost in USD — one-time purchase, valid forever across all rounds
export const VANITY_NAME_COST_USD = 50;

// Pre-defined avatar options
export const AVATAR_OPTIONS = [
  // Row 1 — Faces & personas
  '😎', '🤑', '🥶', '🤯', '👹', '💀', '🤖', '👽',
  // Row 2 — Animals & creatures
  '🐺', '🦊', '🦅', '🐉', '🦍', '🦁', '🐍', '🦈',
  // Row 3 — Power & status
  '👑', '💎', '🔥', '⚡', '🚀', '🌙', '☠️', '🗡️',
  // Row 4 — Vibes & icons
  '🎭', '🃏', '🏆', '⭐', '🎯', '💰', '🧿', '🪬',
];

// Achievement titles based on game stats
export const TITLES: Record<string, { name: string; requirement: string }> = {
  rookie: { name: 'Rookie', requirement: 'First key purchase' },
  player: { name: 'Player', requirement: '10+ keys owned' },
  degen: { name: 'Degen', requirement: '100+ keys owned' },
  whale: { name: 'Whale', requirement: '1000+ keys owned' },
  champion: { name: 'Champion', requirement: 'Won a round' },
  legend: { name: 'Legend', requirement: 'Won 5+ rounds' },
  reaper: { name: 'Reaper', requirement: 'Used Reaper Key' },
  survivor: { name: 'Survivor', requirement: 'Survived Blood Moon' },
  pioneer: { name: 'Pioneer', requirement: 'Played in first 10 rounds' },
};

// ───────────────────────────────────────────────────────────────────────────
// LOCAL CACHE HELPERS
// ───────────────────────────────────────────────────────────────────────────

// Load profiles from localStorage
function loadProfiles(): Map<string, PlayerProfile> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const arr = JSON.parse(stored) as PlayerProfile[];
      return new Map(arr.map(p => [p.address.toLowerCase(), p]));
    }
  } catch (e) {
    console.error('Failed to load profiles:', e);
  }
  return new Map();
}

// Save profiles to localStorage
function saveProfiles(profiles: Map<string, PlayerProfile>): void {
  try {
    const arr = Array.from(profiles.values());
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  } catch (e) {
    console.error('Failed to save profiles:', e);
  }
}

// In-memory cache
let profileCache: Map<string, PlayerProfile> = loadProfiles();

// ───────────────────────────────────────────────────────────────────────────
// WORKER API HELPERS (persistent cross-device storage)
// ───────────────────────────────────────────────────────────────────────────

/**
 * Fetch a player's name from the persistent worker API.
 * Falls back to local cache if API is unreachable.
 */
async function fetchProfileFromAPI(address: string): Promise<PlayerProfile | null> {
  try {
    const res = await fetch(`${VANITY_NAMES_API}/name/${address.toLowerCase()}`, {
      signal: AbortSignal.timeout(5000),
    });
    if (!res.ok) return null;
    const data = await res.json() as any;
    if (!data.found) return null;
    return {
      address: data.address,
      name: data.name,
      avatar: data.avatar,
      paid: data.paid,
      verified: data.paid,
    };
  } catch {
    return null;
  }
}

/**
 * Batch fetch profiles from the worker API.
 */
async function fetchProfilesFromAPI(addresses: string[]): Promise<Map<string, PlayerProfile>> {
  const result = new Map<string, PlayerProfile>();
  if (addresses.length === 0) return result;

  try {
    const cleaned = addresses.map(a => a.toLowerCase()).join(',');
    const res = await fetch(`${VANITY_NAMES_API}/names?addresses=${cleaned}`, {
      signal: AbortSignal.timeout(8000),
    });
    if (!res.ok) return result;
    const data = await res.json() as any;
    const profiles = data.profiles || {};

    for (const [addr, profile] of Object.entries(profiles)) {
      if (profile) {
        const p = profile as any;
        result.set(addr, {
          address: addr,
          name: p.name,
          avatar: p.avatar,
          paid: p.paid,
          verified: p.paid,
        });
      }
    }
  } catch {
    // Fall back to local cache
  }

  return result;
}

/**
 * Save/register a name via the worker API for persistent storage.
 */
async function saveProfileToAPI(
  address: string,
  name: string,
  avatar: string,
  txHash: string,
  signature: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch(`${VANITY_NAMES_API}/name`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address, name, avatar, txHash, signature }),
      signal: AbortSignal.timeout(15000),
    });
    const data = await res.json() as any;
    if (!res.ok) {
      return { success: false, error: data.error || 'Registration failed' };
    }
    return { success: true };
  } catch {
    return { success: false, error: 'Network error — name saved locally' };
  }
}

// ───────────────────────────────────────────────────────────────────────────
// MAIN HOOK
// ───────────────────────────────────────────────────────────────────────────

export function useVanityNames() {
  const [myProfile, setMyProfile] = useState<PlayerProfile | null>(() => {
    try {
      const stored = localStorage.getItem(MY_PROFILE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [profiles, setProfiles] = useState<Map<string, PlayerProfile>>(profileCache);
  const syncedRef = useRef(false);

  // Sync cache changes to localStorage
  useEffect(() => {
    profileCache = profiles;
    saveProfiles(profiles);
  }, [profiles]);

  // Save my profile locally
  useEffect(() => {
    if (myProfile) {
      localStorage.setItem(MY_PROFILE_KEY, JSON.stringify(myProfile));
      // Also add to global profiles
      setProfiles(prev => {
        const next = new Map(prev);
        next.set(myProfile.address.toLowerCase(), myProfile);
        return next;
      });
    }
  }, [myProfile]);

  // On mount: sync my profile from the persistent API
  // This restores names when localStorage is cleared or on a new device
  useEffect(() => {
    if (syncedRef.current) return;
    syncedRef.current = true;

    const syncMyProfile = async () => {
      if (!myProfile?.address) return;

      // Try to fetch from API (in case localStorage was cleared)
      const apiProfile = await fetchProfileFromAPI(myProfile.address);
      if (apiProfile && apiProfile.paid) {
        // API has a verified profile — merge/update local
        const merged: PlayerProfile = {
          ...myProfile,
          ...apiProfile,
          avatar: apiProfile.avatar || myProfile.avatar,
        };
        setMyProfile(merged);
      }
    };

    syncMyProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Restore profile from API when wallet connects.
   * Handles cross-device / cross-browser scenarios.
   * Call this when a wallet connects to check if they already have a name.
   */
  const restoreProfile = useCallback(async (address: string): Promise<PlayerProfile | null> => {
    // Check local cache first
    const local = profiles.get(address.toLowerCase());
    if (local?.paid) return local;

    // Check stored "my profile"
    if (myProfile?.address?.toLowerCase() === address.toLowerCase() && myProfile.paid) {
      return myProfile;
    }

    // Fetch from worker API
    const apiProfile = await fetchProfileFromAPI(address);
    if (apiProfile?.paid) {
      // Restore locally
      setMyProfile(apiProfile);
      return apiProfile;
    }

    return null;
  }, [profiles, myProfile]);

  // Get display name for an address
  const getDisplayName = useCallback((address: string, maxLength: number = 20): string => {
    if (!address) return 'Unknown';

    const profile = profiles.get(address.toLowerCase());
    if (profile?.name) {
      const name = profile.name;
      if (name.length > maxLength) {
        return name.slice(0, maxLength - 3) + '...';
      }
      return name;
    }

    // Fallback to truncated address
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }, [profiles]);

  // Get full profile
  const getProfile = useCallback((address: string): PlayerProfile | null => {
    return profiles.get(address.toLowerCase()) || null;
  }, [profiles]);

  // Get avatar for address
  const getAvatar = useCallback((address: string): string => {
    const profile = profiles.get(address.toLowerCase());
    if (profile?.avatar) return profile.avatar;

    // Generate deterministic emoji from address
    const index = parseInt(address.slice(-4), 16) % AVATAR_OPTIONS.length;
    return AVATAR_OPTIONS[index];
  }, [profiles]);

  /**
   * Batch-fetch profiles for addresses we haven't seen.
   * Use this for leaderboards, activity feeds, etc.
   */
  const fetchProfiles = useCallback(async (addresses: string[]) => {
    // Filter to only unknown addresses
    const unknown = addresses.filter(a => !profiles.has(a.toLowerCase()));
    if (unknown.length === 0) return;

    const fetched = await fetchProfilesFromAPI(unknown);
    if (fetched.size > 0) {
      setProfiles(prev => {
        const next = new Map(prev);
        for (const [addr, profile] of fetched) {
          next.set(addr, profile);
        }
        return next;
      });
    }
  }, [profiles]);

  /**
   * Set my profile (requires txHash from PAW payment for first-time).
   * After initial payment, edits are FREE forever — name persists across all rounds.
   * Syncs to the worker API for cross-device persistence.
   */
  const setProfile = useCallback(async (
    address: string,
    name: string,
    avatar?: string,
    txHash?: string
  ): Promise<PlayerProfile> => {
    // Validate name
    const cleanName = name.trim().slice(0, 20);
    if (cleanName.length < 2) {
      throw new Error('Name must be at least 2 characters');
    }

    // Check for inappropriate content (basic filter)
    const badWords = ['fuck', 'shit', 'ass', 'damn', 'bitch'];
    if (badWords.some(w => cleanName.toLowerCase().includes(w))) {
      throw new Error('Please choose an appropriate name');
    }

    // Check if already paid (allow free edits after initial payment)
    const existing = profiles.get(address.toLowerCase());
    const isPaid = existing?.paid || !!txHash;

    if (!isPaid) {
      throw new Error('Payment required to set a vanity name');
    }

    const finalAvatar = avatar || getAvatar(address);
    const finalTxHash = txHash || existing?.txHash || '';

    // Save to worker API (persistent cross-device storage)
    const apiResult = await saveProfileToAPI(
      address.toLowerCase(),
      cleanName,
      finalAvatar,
      finalTxHash,
      '' // signature placeholder
    );

    // Even if API fails, save locally (graceful degradation)
    if (!apiResult.success) {
      console.warn('API save failed, saving locally only:', apiResult.error);
    }

    const profile: PlayerProfile = {
      address: address.toLowerCase(),
      name: cleanName,
      avatar: finalAvatar,
      paid: true,
      txHash: finalTxHash,
    };

    setMyProfile(profile);
    return profile;
  }, [getAvatar, profiles]);

  // Update my avatar
  const setAvatar = useCallback((avatar: string) => {
    if (!myProfile) return;
    setMyProfile({ ...myProfile, avatar });
  }, [myProfile]);

  // Update title based on achievements
  const updateTitle = useCallback((address: string, title: keyof typeof TITLES) => {
    const existing = profiles.get(address.toLowerCase());
    if (existing) {
      const updated = { ...existing, title: TITLES[title].name };
      setProfiles(prev => {
        const next = new Map(prev);
        next.set(address.toLowerCase(), updated);
        return next;
      });
    }
  }, [profiles]);

  // Check if address has a custom name
  const hasCustomName = useCallback((address: string): boolean => {
    const profile = profiles.get(address.toLowerCase());
    return !!profile?.name;
  }, [profiles]);

  return {
    myProfile,
    profiles,
    getDisplayName,
    getProfile,
    getAvatar,
    setProfile,
    setAvatar,
    updateTitle,
    hasCustomName,
    fetchProfiles,    // Batch-fetch for leaderboards etc.
    restoreProfile,   // Restore on wallet connect (cross-device)
  };
}

// Component for displaying a player name with avatar
export interface PlayerNameProps {
  address: string;
  showAvatar?: boolean;
  showAddress?: boolean;
  className?: string;
  maxLength?: number;
}

// Format address for display (utility)
export function formatAddress(address: string): string {
  if (!address) return 'Unknown';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}
